import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import ProtectedRoute from './components/admin/ProtectedRoute'
import { Toaster } from './components/ui/toaster'
import { useAuthSync } from './hooks/useAuthSync'
import { useLanguageDirection } from './hooks/useLanguageDirection'
import Branches from './pages/admin/Branches'
import Dashboard from './pages/admin/Dashboard'
import Dishes from './pages/admin/Dishes'
import Login from './pages/admin/Login'
import MenuItems from './pages/admin/MenuItems'
import Orders from './pages/admin/Orders'
import Restaurants from './pages/admin/Restaurants'
import Menu from './pages/customer/Menu'
import OrderReview from './pages/customer/OrderReview'
import Chat from './pages/customer/Chat'
import Voice from './pages/Voice'

function App() {
  // Sync Clerk state with Zustand store
  useAuthSync()

  // Apply RTL/LTR direction based on current language (app-wide)
  useLanguageDirection()

  // Apply Slate Refinement theme to document root and detect system dark mode preference
  useEffect(() => {
    const root = document.documentElement
    root.setAttribute('data-theme', 'slate-refinement')
    
    // Check for saved user preference or default to system preference
    const savedMode = localStorage.getItem('theme-mode') as 'light' | 'dark' | null
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    const mode = savedMode || (prefersDark ? 'dark' : 'light')
    
    // Apply mode
    root.setAttribute('data-mode', mode)
    
    // Add/remove .dark class for Tailwind compatibility
    if (mode === 'dark') {
      root.classList.add('dark')
      root.classList.add('dark-mode')
    } else {
      root.classList.remove('dark')
      root.classList.remove('dark-mode')
    }
    
    // Listen for system preference changes (if no user preference is set)
    if (!savedMode) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handleChange = (e: MediaQueryListEvent) => {
        const newMode = e.matches ? 'dark' : 'light'
        root.setAttribute('data-mode', newMode)
        if (newMode === 'dark') {
          root.classList.add('dark')
          root.classList.add('dark-mode')
        } else {
          root.classList.remove('dark')
          root.classList.remove('dark-mode')
        }
      }
      
      // Modern browsers
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange)
        return () => mediaQuery.removeEventListener('change', handleChange)
      }
      // Fallback for older browsers
      else if (mediaQuery.addListener) {
        mediaQuery.addListener(handleChange)
        return () => mediaQuery.removeListener(handleChange)
      }
    }
  }, [])

  return (
    <BrowserRouter>
      <Toaster />
      <Routes>
        <Route path="/" element={<Navigate to="/admin/login" replace />} />
        
        {/* Public customer routes - no authentication required */}
        <Route path="/voice" element={<Voice />} />
        <Route path="/menu" element={<Menu />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/order/review" element={<OrderReview />} />
        
        {/* Catch-all route for Clerk's SignIn component sub-routes (MFA, etc.) */}
        {/* Clerk handles: /admin/login/factor-one, /admin/login/factor-two, /admin/login/continue, etc. */}
        <Route path="/admin/login/*" element={<Login />} />
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/restaurants"
          element={
            <ProtectedRoute>
              <Restaurants />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/branches"
          element={
            <ProtectedRoute>
              <Branches />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/menu-items"
          element={
            <ProtectedRoute>
              <MenuItems />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/dishes"
          element={
            <ProtectedRoute>
              <Dishes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/orders"
          element={
            <ProtectedRoute>
              <Orders />
            </ProtectedRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  )
}

export default App

